import vm from "../main";
import axios from "./PointrCloudAxios";
import ToastHelpers from "@/helpers/ToastHelpers";

export default class LevelService {
  static async getLevels(clientInternalIdentifier, siteInternalIdentifier, buildingInternalIdentifier) {
    let response;
    let levels;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels`
      );
    } catch (error) {
      console.error(error);
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    levels = response?.data?.features;
    if (levels?.length) {
      levels = levels.sort((a, b) => a.properties.lvl - b.properties.lvl);
    }
    return levels;
  }

  static async createLevel(clientInternalIdentifier, siteInternalIdentifier, buildingInternalIdentifier, level) {
    let response;
    try {
      response = await axios.post(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels`,
        level
      );
    } catch (error) {
      console.error(error.message);
      ToastHelpers.createErrorToast(error.response?.data?.message || error.message);
      return error.message;
    }
    return response?.data;
  }

  static async createFloorPlanJob(
    clientInternalIdentifier,
    siteInternalIdentifier,
    buildingInternalIdentifier,
    levelIndex,
    payload
  ) {
    let response;
    // To prevent mock error, a valid bid is required. Todo: Remove comments when BE is ready.
    try {
      response = await axios.post(
        `/mapscale/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}/floor-plan-jobs`,
        payload
      );
    } catch (error) {
      console.error(error);
      ToastHelpers.createServiceErrorToast(error.response, error.response?.data?.message);
    }
    return response?.data;
  }

  static async updateLevel(
    clientInternalIdentifier,
    siteInternalIdentifier,
    buildingInternalIdentifier,
    levelIndex,
    payload,
    abortSignal
  ) {
    let response;

    try {
      response = await axios.put(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}`,
        payload,
        abortSignal ? { signal: abortSignal } : {}
      );
    } catch (error) {
      console.error(error);
      ToastHelpers.createErrorToast(error.response?.data?.message || error.message);
      throw error?.response?.data;
    }
    return response?.data;
  }

  static async deleteLevel(clientInternalIdentifier, siteInternalIdentifier, buildingInternalIdentifier, levelIndex) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.delete(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}`
        // `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/00a2f596-8fc1-4ce2-aec0-bcdd11c10ec9/levels/${levelIndex}`
      );
    } catch (error) {
      console.error(error);
      throw error?.response?.data;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async uploadFile(clientInternalIdentifier, file, onProgress) {
    if (!file) {
      console.error("No file provided.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`utility/clients/${clientInternalIdentifier}/upload-file`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = progressEvent.loaded / progressEvent.total;
            onProgress(percentCompleted);
          }
        }
      });

      if (response.data?.fileUrl) {
        return response.data.fileUrl;
      } else {
        console.warn("File uploaded, but response is missing fileUrl.");
      }
    } catch (error) {
      console.error("Error uploading file:", error.response?.data?.message || error.message);
      throw error?.response?.data;
    }
  }

  static async validateFile(clientInternalIdentifier, payload) {
    if (!payload.fileType || !payload.inputFileUrl) {
      console.error("Both fileType and inputFileUrl are required.");
      return;
    }

    try {
      const response = await axios.post(`/tools/clients/${clientInternalIdentifier}/validate-cad`, payload);

      if (response.data?.message) {
        return response.data.message;
      } else {
        console.warn("Validation response is missing a message.");
      }
    } catch (error) {
      console.error("Error validating CAD file:", error.response?.data?.message || error.message);
      throw error?.response?.data;
    }
  }

  static async cadToGeojson(clientInternalIdentifier, payload) {
    let response;
    try {
      response = await axios.post(`/tools/clients/${clientInternalIdentifier}/cad-to-geojson`, payload);
    } catch (error) {
      console.error(error);
      throw error?.response?.data;
    }
    return response?.data;
  }

  static async geoTransform(clientInternalIdentifier, payload) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.post(`tools/clients/${clientInternalIdentifier}/geotransform`, payload);

      return response.data;
    } catch (error) {
      response = error.response;
      ToastHelpers.createServiceErrorToast(response?.data, vm.$t("contents.levels.geotransform-failed"));
      console.error(error);
      return error?.response?.data;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
  }

  static async cancelMapJob(
    clientInternalIdentifier,
    siteInternalIdentifier,
    buildingInternalIdentifier,
    levelIndex,
    jobId,
    message
  ) {
    let response;
    try {
      response = await axios.post(
        `/mapscale/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}/floor-plan-jobs/${jobId}/cancel`,
        {
          message
        }
      );
    } catch (error) {
      console.error(error);
      ToastHelpers.createServiceErrorToast(error?.response?.data, vm.$t("contents.picker.try-again"));

      return error?.response?.data;
    }

    return response?.data;
  }

  static async changeAnchors(clientInternalIdentifier, request) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.post(`tools/clients/${clientInternalIdentifier}/change-anchors`, request);
    } catch (error) {
      response = error.response;
      console.error(error);
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async calculateAnchors(clientInternalIdentifier, request, abortSignal) {
    let response;
    try {
      response = await axios.post(
        `tools/clients/${clientInternalIdentifier}/calculate-anchors`,
        request,
        abortSignal ? { signal: abortSignal } : {}
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
    return response?.data;
  }

  static async getLevelStatuses(clientInternalIdentifier, siteInternalIdentifier, buildingInternalIdentifier) {
    let response;
    try {
      response = await axios.get(
        `/mapscale/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/floor-plan-jobs?skip=0&take=1000`
      );
    } catch (error) {
      response = error.response;
      console.error(error);
      throw error;
    }
    return response?.data;
  }

  static async geojsonToPng(clientInternalIdentifier, request, abortSignal) {
    let response;
    try {
      response = await axios.post(
        `tools/clients/${clientInternalIdentifier}/geojson-to-png`,
        request,
        abortSignal ? { signal: abortSignal } : {}
      );
      return response.data;
    } catch (error) {
      console.error("geojsonToPng error:", error);
      throw error;
    }
  }
}
